import {
  Asset,
  FeatureName,
  Issue,
  SortDirectionEnum,
  useInfiniteFindByQuery,
  useIsFeatureEnabled,
  useUpdateMutation,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Pressable,
  StyleSheet,
  View
} from 'react-native';
import { RadioButton, Text, TextInput } from 'react-native-paper';
import { Colors } from '../../themes';
import { colors } from '../crop-circle/tokens';
import { DebouncedTextInput, SaveFormBottom } from '../forms';
import LoadingIndicator from '../loadingIndicator';
import { ReassignModal as ReassignModalOld } from './reassignModal-old';

type ReassignModalProps = {
  visible: boolean;
  hide: () => void;
  issue: Issue;
  presentWebStyle: boolean;
  bottomBar?: React.ComponentType<any>;
};

export const ReassignModal = (props: ReassignModalProps) => {
  const isCropCircle = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  if (isCropCircle) {
    return <ReassignModalBase {...props} />;
  }

  return <ReassignModalOld {...props} />;
};

export const ReassignModalBase = ({
  hide,
  issue,
  bottomBar,
}: ReassignModalProps) => {
  const PAGE_SIZE = 30;
  const { t } = useTranslation();
  const defaultFilter = {
    ...(issue.asset && { id: { ne: issue.asset.id } }),
    workspaces: { eq: issue.workspace.id },
  };
  const defaultValue = issue.asset?.id;
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [assetSearch, setAssetSearch] = useState('');
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    defaultValue
  );
  const [isDirty, setIsDirty] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteFindByQuery({
      modelName: 'Asset',
      query: FIND_ASSETS,
      filter: filter,
      orderBy: {
        field: 'name',
        order: SortDirectionEnum.Asc,
      },
      pageSize: PAGE_SIZE,
    });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(data?.pages?.length || 0);
  // if a user goes back and then revisits this page, data might be cached and we could already have more than 1 page
  const initialPages = data?.pages?.length || 0;

  const { mutateAsync: updateIssue } = useUpdateMutation({
    modelName: 'Issue',
    query: UPDATE_ISSUE,
  });

  useEffect(() => {
    if (
      isLoading ||
      isFetchingNextPage ||
      !data ||
      !data.pages ||
      data.pages.length === 0
    )
      return;

    if (page > 0 && page > initialPages) {
      const pageItems = data.pages[page]?.items ?? [];
      setAssets((prevAssets) => [...prevAssets, ...pageItems]);
    } else {
      setTotalCount(data.pages[0].count);
      setAssets(data.pages.flatMap((page: any) => page?.items ?? []));
    }
  }, [initialPages, data, isFetchingNextPage, isLoading, page]);

  const onEndReached = () => {
    if (!totalCount || totalCount <= PAGE_SIZE) return;
    if (!isFetchingNextPage && hasNextPage) {
      setPage(page + 1);
      fetchNextPage();
    }
  };

  const updateSearch = (text: string) => {
    const textFilter = { contains: text };
    setFilter({
      ...defaultFilter,
      and: [
        {
          or: [{ name: textFilter }, { description: textFilter }],
        },
      ],
    });
  };

  const onItemSelected = async (assetId?: string) => {
    setIsDirty(assetId !== defaultValue);
    setCurrentValue(assetId);
  };


  const onSubmit = async () => {
    const input = {
      id: issue.id,
      asset: { id: currentValue },
    };

    await updateIssue(
      { input: input },
      {
        onSettled: () => {
          if (hide) {
            hide();
          }
          setIsDirty(false);
        },
      }
    );
  };

  // Reset form to initial asset and dirty state
  const onResetForm = () => {
    setCurrentValue(defaultValue);
    setIsDirty(false);
    setAssetSearch('');
    setFilter(defaultFilter);
  };

  const BottomBar = bottomBar;

  return (
    <SaveFormBottom
      isDirty={isDirty}
      onSubmit={onSubmit}
      title={t('issue.editModal.header')}
      saveLabel={t('issue.editModal.save')}
      onResetForm={onResetForm}
      bottomBar={(props) => bottomBar ? <BottomBar {...props} isDirty={isDirty} /> : null}
    >
      <View style={styles.formContainer}>
        <View style={styles.headerContainer}>
          <View style={styles.titleContainer}>
            <Text style={styles.titleLabel}>
              {t('issue.reassign.title')}
            </Text>
            <Text style={styles.instructionsLabel}>
              {t('issue.reassign.instructions')}
            </Text>
          </View>
          <DebouncedTextInput
            debounceTimeMs={500}
            debounceLength={3}
            debounceFunc={updateSearch}
            onBlur={() => {}}
            height={48}
            value={assetSearch}
            onChangeText={setAssetSearch}
            placeholder={t('issue.reassign.placeholder')}
            left={<TextInput.Icon icon="magnify" color={'#A1A8B5'} />}
            outlineStyle={styles.inputStyle}
            disableInnerStyle={true}
          />
        </View>

        <View style={styles.contentContainer}>
          <View style={styles.listContainer}>
            {/* show items from search */}
            {isLoading ? (
              <LoadingIndicator
                backgroundColor={'transparent'}
                flexContainer={true}
              />
            ) : (
              <>
                {/* show existing*/}
                {issue.asset ? (
                  <AssetItem
                    onItemSelected={onItemSelected}
                    currentValue={currentValue}
                    asset={issue.asset}
                  />
                ) : (
                  <Item
                    onItemSelected={onItemSelected}
                    currentValue={currentValue}
                  >
                    <Text numberOfLines={1} style={styles.itemAssetLabel}>
                      {issue.workspace.name}
                    </Text>
                  </Item>
                )}
                <FlatList
                  refreshing={isFetchingNextPage}
                  style={styles.scrollViewContainer}
                  data={assets}
                  extraData={assets}
                  onEndReached={onEndReached}
                  renderItem={({ item }) => (
                    <AssetItem
                      onItemSelected={onItemSelected}
                      currentValue={currentValue}
                      asset={item}
                    />
                  )}
                />
              </>
            )}
          </View>
        </View>
      </View>
    </SaveFormBottom>
  );
};

type BaseItemProps = {
  onItemSelected: (item?: string) => Promise<void>;
  currentValue?: string;
};

type ItemProps = BaseItemProps & {
  children: ReactNode;
  value?: string;
};

const Item = ({ onItemSelected, currentValue, children, value }: ItemProps) => {
  return (
    <Pressable
      style={styles.itemContainer}
      onPress={() => onItemSelected(value)}
    >
      {children}
      <RadioButton.Android
        value={value || ''}
        status={currentValue === value ? 'checked' : 'unchecked'}
        onPress={() => onItemSelected(value)}
        color={'#020202'}
        uncheckedColor={'#A1A8B5'}
      />
    </Pressable>
  );
};

type AssetItemProps = BaseItemProps & {
  asset: Asset;
};

const AssetItem = ({ asset, onItemSelected, currentValue }: AssetItemProps) => {
  return (
    <Item
      onItemSelected={onItemSelected}
      currentValue={currentValue}
      value={asset.id}
    >
      <View style={styles.itemLabelsContainer}>
        <Text numberOfLines={1} style={styles.itemAssetLabel}>
          {asset.name}
        </Text>
      </View>
    </Item>
  );
};

const styles = StyleSheet.create({
  modalBackground: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: 560,
    height: 600,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#FBFCFF',
  },
  formContainer: {
    width: '100%',
    height: '100%',
    flex: 1,
  },
  headerContainer: {
    backgroundColor: '#FCFCFC',
    padding: 16,
    gap: 16,
    zIndex: 1,
  },
  titleContainer: {
    gap: 4,
  },
  titleLabel: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 24,
    color: '#020202',
  },
  instructionsLabel: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: '#020202',
  },
  inputStyle: {
    borderWidth: 0,
    backgroundColor: '#F3F3F3',
    borderRadius: 4,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#FCFCFC',
    padding: 16,
    justifyContent: 'space-between',
  },
  listContainer: {
    flex: 1,
    gap: 4,
  },
  scrollViewContainer: {
    flex: 1,
    paddingBottom: 100
  },
  itemContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 44,
    paddingVertical: 8,
    paddingHorizontal: 4,
    backgroundColor: colors.neutral[200],
    marginBottom: 4,
  },
  itemLabelsContainer: {
    flex: 1,
    marginRight: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  itemAssetLabel: {
    fontSize: 14,
    color: '#020202',
    fontWeight: '400',
    lineHeight: 18,
  },
  itemDescriptionLabel: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: '#A1A8B5',
  },
  saveButton: {
    height: 56,
    borderRadius: 99,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    marginTop: 16,
  },
  saveButtonEnabled: {
    backgroundColor: '#1A1A1A',
  },
  saveButtonDisabled: {
    backgroundColor: '#ECEEF1',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  saveButtonTextEnabled: {
    color: '#FFFFFF',
  },
  saveButtonTextDisabled: {
    color: '#A1A8B5',
  },
});

const FIND_ASSETS = `
  query FindAssetsForReassignment($filter: AssetFilter, $page: PageRequest, $orderBy: OrderByInput) {
    findAssets(filter: $filter, page: $page, orderBy: $orderBy) {
      items {
        id
        description
        name
      }
      count
      limit
      offset
    }
  }
`;

const UPDATE_ISSUE = `
  mutation UpdateIssueAssignment($input: MutateIssueInput!) {
    updateIssue(input: $input) {
      id
    }
  }
`;
